/**
 * Converts an external image URL to use the Next.js image proxy API
 * This ensures images work properly in production/build mode
 */
export function getProxiedImageUrl(imageUrl: string): string {
  // If it's a local image (starts with /), return as is
  if (imageUrl.startsWith('/')) {
    return imageUrl;
  }

  // For external URLs, use the proxy API
  return `/api/image?url=${encodeURIComponent(imageUrl)}`;
}

/**
 * Checks if the URL is an external image that needs proxying
 */
export function isExternalImage(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * Gets the appropriate image URL based on environment
 * In development, returns the original URL
 * In production, uses the proxy API for external images
 */
export function getImageUrl(imageUrl: string): string {
  if (!imageUrl) return '';
  
  // Local images don't need proxying
  if (!isExternalImage(imageUrl)) {
    return imageUrl;
  }

  // In production or when needed, use the proxy
  // For now, we'll use the proxy for all external images
  return getProxiedImageUrl(imageUrl);
}

/**
 * Converts a model image path to use the serve-local-image API
 * This is needed for dynamically uploaded images that aren't in the build
 */
export function getModelImageUrl(imagePath: string): string {
  if (!imagePath) return '';
  
  // If it's already an API URL or external URL, return as is
  if (imagePath.startsWith('/api/') || isExternalImage(imagePath)) {
    return imagePath;
  }
  
  // For model images that might be dynamically uploaded
  // Use the serve-local-image API to ensure they work in production
  if (imagePath.startsWith('/models/')) {
    return `/api/serve-local-image?path=${encodeURIComponent(imagePath)}`;
  }
  
  // For static images in public folder, return as is
  return imagePath;
}

/**
 * Converts a model video path to use the serve-video API
 * This is needed for dynamically uploaded videos with proper streaming support
 */
export function getModelVideoUrl(videoPath: string): string {
  if (!videoPath) return '';
  
  // If it's already an API URL or external URL, return as is
  if (videoPath.startsWith('/api/') || isExternalImage(videoPath)) {
    return videoPath;
  }
  
  // For model videos that might be dynamically uploaded
  // Use the serve-video API to ensure they work in production with proper streaming
  if (videoPath.startsWith('/models/')) {
    return `/api/serve-video?path=${encodeURIComponent(videoPath)}`;
  }
  
  // For static videos in public folder, return as is
  return videoPath;
}




